import React, { useState } from 'react';
import {
  Show,
  TabbedShowLayout,
  Tab,
  TextField,
  EmailField,
  BooleanField,
  DateField,
  ReferenceField,
  ReferenceManyField,
  Datagrid,
  FunctionField,
  useRecordContext,
  useRedirect,
  NumberField,
  ReferenceInput,
  SelectInput,
  useGetList,
  TopToolbar,
  EditButton,
  ListButton,
  useUpdate,
  useNotify,
  useRefresh,
  usePermissions,
  useGetIdentity,
} from 'react-admin';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
  Button as MuiButton,
  Button,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField as MuiTextField,
  Alert,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Person as PersonIcon,
  LocalShipping as TruckIcon,
  AccessTime as AccessTimeIcon,
  Assignment as AssignmentIcon,
  VerifiedUser as VerifiedIcon,
  Email as EmailIcon,
  AccountBox as AccountBoxIcon,
  Speed as SpeedIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Notifications as RemindersIcon,
  PictureAsPdf as PictureAsPdfIcon,
  GetApp as DownloadIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import ResourceBreadcrumbs from '../../components/ResourceBreadcrumbs';
import { RemindersTab } from '../../components/RemindersTab';
import { DriverProfileTab } from './DriverProfileTab';
import { CompletedFormDialog } from '../../components/dialogs/CompletedFormDialog';
import { DriverTimeLogDialog } from '../../components/dialogs/DriverTimeLogDialog';
import { downloadFileAuthenticated } from '../../utils/downloadFile';

// PDF download helper function
const handleDownloadPdf = async (formId: number, pdfFileId?: number) => {
  try {
    const endpoint = pdfFileId
      ? `/completed-forms/${formId}/pdf`
      : `/completed-forms/${formId}/make-pdf`;
    await downloadFileAuthenticated(endpoint, `completed-form-${formId}.pdf`);
  } catch (error) {
    console.error('PDF download failed:', error);
    alert('Failed to download PDF. Please try again or contact support.');
  }
};

const hasAdminRole = (roles?: string[]) => roles?.includes('admin') ?? false;
const getDisplayRole = (roles?: string[]) => (hasAdminRole(roles) ? 'admin' : (roles?.[0] || 'user'));

const CompletedFormsTab = () => {
  const record = useRecordContext();
  const [showDialog, setShowDialog] = React.useState(false);
  const [selectedFormId, setSelectedFormId] = React.useState<number | null>(null);
  const [formTypeFilter, setFormTypeFilter] = React.useState<number | null>(null);

  // Fetch forms for the dropdown
  const { data: forms } = useGetList('forms', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  });

  const handleRowClick = (id: string | number) => {
    setSelectedFormId(Number(id));
    setShowDialog(true);
    return false; // Prevent default navigation
  };

  if (!record) return null;

  // Build filter object - always include userId, optionally include formId
  const filter = formTypeFilter
    ? { userId: record.id, formId: formTypeFilter }
    : { userId: record.id };

  return (
    <ReferenceManyField
      reference="completed-forms/reports"
      target="userId"
      label={false}
      perPage={10}
      sort={{ field: 'startedAt', order: 'DESC' }}
      filter={filter}
    >
      <Box>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="primary">
            Completed Forms by this User
          </Typography>
        </Box>

        {/* Form Type Filter */}
        <Box sx={{ mb: 2 }}>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Form Type</InputLabel>
            <Select
              value={formTypeFilter || ''}
              onChange={(event) => setFormTypeFilter(event.target.value || null)}
              label="Form Type"
              sx={{
                minWidth: 250,
                height: '56px',
              }}
            >
              <MenuItem value="">All Form Types</MenuItem>
              {forms?.map(form => (
                <MenuItem key={form.id} value={form.id}>
                  {form.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Datagrid
          bulkActionButtons={false}
          rowClick={handleRowClick}
          sx={{
            '& .RaDatagrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },
          }}
          empty={
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No completed forms yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Forms completed by this user will appear here
              </Typography>
            </Box>
          }
        >
          <FunctionField
            label="Started At"
            render={(record: any) => new Date(record.startedAt).toLocaleString()}
            source="startedAt"
          />
          <FunctionField
            label="Completed At"
            render={(record: any) => new Date(record.completedAt).toLocaleString()}
            source="completedAt"
          />
          <FunctionField label="Form Type" render={(record: any) => record.form?.name} />
          <FunctionField
            label="Vehicle/Equipment"
            render={(record: any) => {
              if (!record.equipment) return '-';
              return `${record.equipment.manufacturedYear || ''} ${record.equipment.make || ''} ${record.equipment.model || ''}`.trim() || record.equipment.name;
            }}
          />
          <FunctionField
            label="PDF"
            render={(record: any) => (
              <Tooltip title="Download PDF">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent row click
                    handleDownloadPdf(record.id, record.pdfFileId);
                  }}
                  sx={{ color: record.pdfFileId ? 'success.main' : 'primary.main' }}
                >
                  {record.pdfFileId ? <PictureAsPdfIcon /> : <DownloadIcon />}
                </IconButton>
              </Tooltip>
            )}
          />
        </Datagrid>
        <CompletedFormDialog
          open={showDialog}
          onClose={() => {
            setShowDialog(false);
            setSelectedFormId(null);
          }}
          formId={selectedFormId}
        />
      </Box>
    </ReferenceManyField>
  );
};

const DriverTimeLogsTab = () => {
  const record = useRecordContext();
  const [showDialog, setShowDialog] = React.useState(false);
  const [selectedTimeLogId, setSelectedTimeLogId] = React.useState<number | null>(null);

  const handleRowClick = (id: string | number) => {
    setSelectedTimeLogId(Number(id));
    setShowDialog(true);
    return false; // Prevent default navigation
  };

  if (!record) return null;

  return (
    <ReferenceManyField
      reference="driver-time-logs"
      target="userId"
      sort={{ field: 'startedAt', order: 'DESC' }}
      label={false}
      perPage={10}
    >
      <Box>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="primary">
            Time Logs for this User
          </Typography>
        </Box>
        <Datagrid
          bulkActionButtons={false}
          rowClick={handleRowClick}
          sx={{
            '& .RaDatagrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },
          }}
          empty={
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <AccessTimeIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No time logs yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Time logs for this user will appear here
              </Typography>
            </Box>
          }
        >
          <FunctionField
            label="Started At"
            render={(record: any) => new Date(record.startedAt).toLocaleString()}
            source="startedAt"
          />
          <FunctionField
            label="Driving Time"
            render={(record: any) => (
              <Chip
                label={formatDuration(record.durationDriving)}
                color="primary"
                size="small"
                variant="outlined"
                icon={<TruckIcon />}
              />
            )}
          />
          <FunctionField
            label="On-Duty Time"
            render={(record: any) => (
              <Chip
                label={formatDuration(record.durationOnDuty)}
                color="secondary"
                size="small"
                variant="outlined"
                icon={<AccessTimeIcon />}
              />
            )}
          />
          <FunctionField
            label="Total Duty Time"
            render={(record: any) => formatDuration(record.durationDriving + record.durationOnDuty)}
          />
        </Datagrid>
        <DriverTimeLogDialog
          open={showDialog}
          onClose={() => {
            setShowDialog(false);
            setSelectedTimeLogId(null);
          }}
          timeLogId={selectedTimeLogId}
        />
      </Box>
    </ReferenceManyField>
  );
};

const ShowActions = () => {
  return null;
};

// User/Driver Summary Card - Comprehensive header
const UserSummaryCard = () => {
  const record = useRecordContext();
  
  if (!record) return null;
  
  const isDriver = !!record.driver;
  
  
  return (
    <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={2} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <Avatar
              sx={{
                width: 100,
                height: 100,
                bgcolor: isDriver ? 'primary.main' : 'grey.500',
                fontSize: '2.5rem',
                mx: { xs: 'auto', md: 0 }
              }}
            >
              {record.firstName?.charAt(0)?.toUpperCase() || record.name?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
          </Grid>

          <Grid item xs={12} md={5}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {record.firstName && record.lastName ? `${record.firstName} ${record.lastName}` : (record.name || 'User')}
            </Typography>
            
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
              {record.isActive === false && (
                <Chip
                  icon={<BlockIcon />}
                  label="INACTIVE"
                  color="default"
                  size="small"
                  sx={{ bgcolor: 'grey.700', color: 'white' }}
                />
              )}
              {isDriver && record.driver.profileState && (
                <Chip
                  icon={<TruckIcon />}
                  label={`Driver - ${record.driver.profileState}`}
                  color={record.driver.profileState === 'COMPLETE' ? 'success' : 'warning'}
                  size="small"
                />
              )}
              {record.driver?.hasFastPass && (
                <Chip
                  icon={<SpeedIcon />}
                  label="FAST Pass"
                  color="info"
                  variant="outlined"
                  size="small"
                />
              )}
              {record.isSuperAdmin && (
                <Chip
                  label="SUPER ADMIN"
                  color="secondary"
                  size="small"
                  variant="filled"
                />
              )}
              {record.roles?.map((role: string) => (
                <Chip
                  key={role}
                  label={role.toUpperCase()}
                  color={role === 'admin' ? 'error' : 'default'}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Stack>

            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                <EmailIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                {record.email}
              </Typography>
              {record.phoneNumber && (
                <Typography variant="body2" color="text.secondary">
                  <PhoneIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                  {record.phoneNumber}
                </Typography>
              )}
            </Stack>
          </Grid>

        </Grid>
      </CardContent>
    </Card>
  );
};

// Tab Label with Count
const TabLabelWithCount = ({ title, count, color = '#1976d2' }: { title: string; count?: number; color?: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <span>{title}</span>
    {count !== undefined && (
      <Chip 
        label={count} 
        size="small" 
        sx={{ 
          height: 20,
          minWidth: 28,
          backgroundColor: `${color}20`,
          color: color,
          fontWeight: 'bold',
          '& .MuiChip-label': { px: 1 }
        }} 
      />
    )}
  </Box>
);

// Enhanced Overview Tab with Inline Editing
const OverviewTab = () => {
  const record = useRecordContext();
  const { permissions } = usePermissions();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    phoneExtension: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    province: '',
    postalCode: '',
    roles: [] as string[],
  });
  const [update, { isLoading }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!record) return null;

  // Check if current user is admin or super admin
  const isAdmin = permissions?.roles?.includes('admin') || permissions?.isSuperAdmin;

  // Initialize form data when record loads or editing starts
  React.useEffect(() => {
    if (record) {
      const currentRole = getDisplayRole(record.roles);
      
      setFormData({
        firstName: record.firstName || record.name?.split(' ')[0] || '',
        lastName: record.lastName || record.name?.split(' ').slice(1).join(' ') || '',
        email: record.email || '',
        phoneNumber: record.phoneNumber || '',
        phoneExtension: record.phoneExtension || '',
        addressLine1: record.addressLine1 || '',
        addressLine2: record.addressLine2 || '',
        city: record.city || '',
        province: record.province || '',
        postalCode: record.postalCode || '',
        roles: record.roles?.length ? [...record.roles] : [currentRole],
      });
    }
  }, [record]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form data to original record values
    if (record) {
      const currentRole = getDisplayRole(record.roles);
      
      setFormData({
        firstName: record.firstName || record.name?.split(' ')[0] || '',
        lastName: record.lastName || record.name?.split(' ').slice(1).join(' ') || '',
        email: record.email || '',
        phoneNumber: record.phoneNumber || '',
        phoneExtension: record.phoneExtension || '',
        addressLine1: record.addressLine1 || '',
        addressLine2: record.addressLine2 || '',
        city: record.city || '',
        province: record.province || '',
        postalCode: record.postalCode || '',
        roles: record.roles?.length ? [...record.roles] : [currentRole],
      });
    }
  };

  const handleSave = async () => {
    if (!record?.id) return;

    try {
      // Build the update data object
      const updateData: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber || null,
        phoneExtension: formData.phoneExtension || null,
        addressLine1: formData.addressLine1 || null,
        addressLine2: formData.addressLine2 || null,
        city: formData.city || null,
        province: formData.province || null,
        postalCode: formData.postalCode || null,
      };

      // Only include roles if the user is an admin
      if (isAdmin) {
        updateData.roles = formData.roles.length > 0 ? formData.roles : ['user'];
      }

      await update(
        'users',
        {
          id: record.id,
          data: updateData,
        },
        {
          onSuccess: () => {
            notify('User updated successfully', { type: 'success' });
            refresh();
            setIsEditing(false);
          },
          onError: (error: any) => {
            notify(error?.message || 'Failed to update user', { type: 'error' });
          },
        }
      );
    } catch (error) {
      notify('Failed to update user', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Personal Information */}
      <Card sx={{ mb: 3, overflow: 'visible', border: '1px solid #e0e0e0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ fontSize: 28, color: '#1976d2', mr: 2 }} />
              <Typography variant="h5" fontWeight="bold" color="primary">
                Personal Information
              </Typography>
            </Box>
            {!isEditing && (
              <Button
                startIcon={<EditIcon />}
                variant="outlined"
                onClick={handleEdit}
                size="small"
              >
                Edit
              </Button>
            )}
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">First Name</Typography>
                  {isEditing ? (
                    <MuiTextField
                      fullWidth
                      size="small"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      required
                    />
                  ) : (
                    <Typography variant="body1">{record.firstName || record.name || ''}</Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Last Name</Typography>
                  {isEditing ? (
                    <MuiTextField
                      fullWidth
                      size="small"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      required
                    />
                  ) : (
                    <Typography variant="body1">{record.lastName || ''}</Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Email Address</Typography>
                  {isEditing ? (
                    <MuiTextField
                      fullWidth
                      size="small"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  ) : (
                    <Typography variant="body1">{record.email || '—'}</Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Phone Number</Typography>
                  {isEditing ? (
                    <MuiTextField
                      fullWidth
                      size="small"
                      value={formData.phoneNumber}
                      onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                      placeholder="Not provided"
                    />
                  ) : (
                    <Typography variant="body1">{record.phoneNumber || 'Not provided'}</Typography>
                  )}
                </Box>
                {isEditing && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Phone Extension</Typography>
                    <MuiTextField
                      fullWidth
                      size="small"
                      value={formData.phoneExtension}
                      onChange={(e) => setFormData({ ...formData, phoneExtension: e.target.value })}
                    />
                  </Box>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                {isAdmin && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Permission Role</Typography>
                    {isEditing ? (
                      <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                        <Select
                          value={hasAdminRole(formData.roles) ? 'admin' : 'user'}
                          onChange={(e) => setFormData({ ...formData, roles: [e.target.value as string] })}
                        >
                          <MenuItem value="user">User</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                        {record.isSuperAdmin && (
                          <Chip
                            label="SUPER ADMIN"
                            size="small"
                            color="secondary"
                            variant="filled"
                          />
                        )}
                        <Chip 
                          label={getDisplayRole(record.roles).toUpperCase()}
                          size="small" 
                          color={hasAdminRole(record.roles) ? 'error' : 'primary'}
                          variant="outlined" 
                        />
                      </Stack>
                    )}
                  </Box>
                )}
                {!isEditing && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Account Created</Typography>
                    <Typography variant="body1">
                      {new Date(record.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Grid>
          </Grid>

          {isEditing && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>
                Address Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <MuiTextField
                    fullWidth
                    size="small"
                    label="Address Line 1"
                    value={formData.addressLine1}
                    onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MuiTextField
                    fullWidth
                    size="small"
                    label="Address Line 2"
                    value={formData.addressLine2}
                    onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MuiTextField
                    fullWidth
                    size="small"
                    label="City"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MuiTextField
                    fullWidth
                    size="small"
                    label="Province/State"
                    value={formData.province}
                    onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MuiTextField
                    fullWidth
                    size="small"
                    label="Postal/ZIP Code"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                  />
                </Grid>
              </Grid>
            </>
          )}

          {isEditing && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  startIcon={<CancelIcon />}
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  startIcon={<SaveIcon />}
                  variant="contained"
                  onClick={handleSave}
                  disabled={isLoading}
                >
                  Save
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Set Password - Admin Only */}
      {isAdmin && <SetPasswordCard />}

      {/* Deactivate/Reactivate Account - Admin Only */}
      {isAdmin && <DeactivateAccountCard />}
    </Box>
  );
};

// Deactivate/Reactivate Account Card - Admin only
const DeactivateAccountCard = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const { data: identity, isPending: identityPending } = useGetIdentity();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!record) return null;

  // Treat an unresolved identity as "self" (safe default) so the action
  // button can't render, and the confirm dialog can't be opened, during the
  // brief window before identity has loaded.
  const isSelf = identityPending || (identity?.id != null && String(identity.id) === String(record.id));
  const isActive = record.isActive !== false;

  const handleToggle = async () => {
    if (isSelf) return; // defense in depth: the dialog is only reachable when !isSelf, but guard anyway
    setConfirmOpen(false);
    setIsSubmitting(true);
    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const token = localStorage.getItem('accessToken');
      const action = isActive ? 'deactivate' : 'reactivate';
      const response = await fetch(`${baseUrl}/users/${record.id}/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to ${action} account`);
      }

      notify(isActive ? 'Account deactivated' : 'Account reactivated', { type: 'success' });
      refresh();
    } catch (error: any) {
      notify(error.message || 'Failed to update account status', { type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card sx={{ mb: 3, overflow: 'visible', border: '1px solid #e0e0e0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {isActive ? (
            <BlockIcon sx={{ fontSize: 28, color: 'error.main', mr: 2 }} />
          ) : (
            <CheckCircleIcon sx={{ fontSize: 28, color: 'success.main', mr: 2 }} />
          )}
          <Typography variant="h5" fontWeight="bold" color={isActive ? 'error' : 'success.main'}>
            Account Status
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isActive
            ? 'Deactivating this account blocks the user from logging in. Their driver files and records are kept and can be restored at any time by reactivating the account.'
            : `This account is deactivated and cannot log in.${record.deactivatedAt ? ` Deactivated on ${new Date(record.deactivatedAt).toLocaleDateString()}.` : ''} Their driver files and records have been retained.`}
        </Typography>
        {isSelf ? (
          <Alert severity="info">You cannot deactivate your own account.</Alert>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color={isActive ? 'error' : 'success'}
                startIcon={isActive ? <BlockIcon /> : <CheckCircleIcon />}
                onClick={() => setConfirmOpen(true)}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Updating...' : isActive ? 'Deactivate Account' : 'Reactivate Account'}
              </Button>
            </Box>
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
              <DialogTitle>{isActive ? 'Deactivate this account?' : 'Reactivate this account?'}</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {isActive
                    ? `${record.firstName || record.email} will immediately be signed out and unable to log in. Their driver files and records will not be deleted.`
                    : `${record.firstName || record.email} will be able to log in again immediately.`}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                <Button onClick={handleToggle} color={isActive ? 'error' : 'success'} variant="contained" autoFocus>
                  {isActive ? 'Deactivate' : 'Reactivate'}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Set Password Card - Admin only
const SetPasswordCard = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!record) return null;

  const handleSetPassword = async () => {
    if (!password || password.length < 8) {
      notify('Password must be at least 8 characters', { type: 'error' });
      return;
    }
    if (password !== confirmPassword) {
      notify('Passwords do not match', { type: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${baseUrl}/users/${record.id}/set-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to set password');
      }

      notify('Password updated successfully', { type: 'success' });
      setPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      notify(error.message || 'Failed to set password', { type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card sx={{ mb: 3, overflow: 'visible', border: '1px solid #e0e0e0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <LockIcon sx={{ fontSize: 28, color: '#1976d2', mr: 2 }} />
          <Typography variant="h5" fontWeight="bold" color="primary">
            Set Password
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Set a new password for this user. They will be able to log in with this password immediately.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <MuiTextField
              fullWidth
              size="small"
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              helperText="Minimum 8 characters"
              disabled={isSubmitting}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <MuiTextField
              fullWidth
              size="small"
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isSubmitting}
              error={confirmPassword.length > 0 && password !== confirmPassword}
              helperText={confirmPassword.length > 0 && password !== confirmPassword ? 'Passwords do not match' : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      size="small"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<LockIcon />}
            onClick={handleSetPassword}
            disabled={isSubmitting || !password || !confirmPassword || password !== confirmPassword || password.length < 8}
          >
            {isSubmitting ? 'Setting...' : 'Set Password'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

// Format duration helper
const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours === 0) {
    return `${minutes}m`;
  }
  
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
};


const UserShowContent = () => {
  return (
    <>
      <ResourceBreadcrumbs
        customNames={{
          singular: 'Team Member',
          plural: 'Team Members'
        }}
        resourcePath="users"
      />
      <UserSummaryCard />

      <TabbedShowLayout>
        <Tab label="OVERVIEW" icon={<PersonIcon />}>
          <OverviewTab />
        </Tab>

        {/* Driver Profile Tab - moved to second position for better UX */}
        <Tab
          label={<TabLabelWithCount title="DRIVER PROFILE" color="#2e7d32" />}
          icon={<TruckIcon sx={{ color: '#2e7d32' }} />}
        >
          <DriverProfileTab />
        </Tab>

        <Tab
          label={<TabLabelWithCount title="TIME LOGS" color="#1976d2" />}
          icon={<AccessTimeIcon sx={{ color: '#1976d2' }} />}
        >
          <DriverTimeLogsTab />
        </Tab>

        <Tab
          label={<TabLabelWithCount title="COMPLETED FORMS" color="#4caf50" />}
          icon={<AssignmentIcon sx={{ color: '#4caf50' }} />}
        >
          <CompletedFormsTab />
        </Tab>

        <Tab
          label={<TabLabelWithCount title="REMINDERS" color="#d32f2f" />}
          icon={<RemindersIcon sx={{ color: '#d32f2f' }} />}
        >
          <RemindersTab context="user" />
        </Tab>
      </TabbedShowLayout>
    </>
  );
};

export const UserShowEnhanced = () => {
  return (
    <Show actions={<ShowActions />}>
      <UserShowContent />
    </Show>
  );
};

export { UserShowEnhanced as UserShow };